import {
  capitalize,
  colors,
  CurrencyCode,
  getCurrencySymbol,
  IOrganisationResponse,
  IPromotionResponse,
  PromotionStatus,
  PromotionType,
} from '@one-tree/library'
import React, { ReactElement } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { IOrganisationPatchBody } from '../../../types/API'

export type Promotion = Omit<IPromotionResponse, 'activeFrom' | 'deactivateOn' | 'validityStart' | 'validityEnd'> & {
  activeFrom?: string
  deactivateOn?: string
  validityStart?: string
  validityEnd?: string
}
export type UpdatePromotion = Partial<Omit<Promotion, 'id' | 'sales'>>
export type Promotions = Promotion[]

export function convertPromoResponseToPromotion(
  promo: IPromotionResponse,
): Promotion {
  // API now returns GMT strings, type mismatch requires cast
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  return {
    ...promo,
  } as Promotion
}

export function convertPromotionToPromoResponse(promo: Promotion): IPromotionResponse {
  // eslint-disable-next-line prefer-object-spread
  const result = {
    ...promo,
    activeFrom: promo.activeFrom
      ? moment(promo.activeFrom, 'YYYY-MM-DD HH:mm:ss').unix()
      : undefined,
    deactivateOn: promo.deactivateOn
      ? moment(promo.deactivateOn, 'YYYY-MM-DD HH:mm:ss').unix()
      : undefined,
    validityStart: promo.validityStart
      ? moment(promo.validityStart, 'YYYY-MM-DD').unix()
      : undefined,
    validityEnd: promo.validityEnd
      ? moment(promo.validityEnd, 'YYYY-MM-DD').unix()
      : undefined,
  }
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  return result as IPromotionResponse
}

export const PromoOption = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`
export const PromoDot = styled.span<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ active }): string => (active ? colors.green : colors.darkGray)};
  flex-shrink: 0;
  margin-left: 2px;
`
export type Org = IOrganisationResponse
export type UpdateOrg = IOrganisationPatchBody

export function getDateAndTime(dateTime?: string): ReactElement | string {
  if (!dateTime) return '-'

  const momentDate = moment(dateTime, 'YYYY-MM-DD HH:mm:ss')
  const date = momentDate.format('DD/MM/YYYY')
  const time = momentDate.format('HH:mm')
  const word = <span style={{ color: colors.darkerGray }}>at </span>

  return (
    <span>
      {date}{' '}
      <span style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
        {word}
        {time}
      </span>
    </span>
  )
}

function formatValidityDate(
  date: string | number | undefined,
  isString: boolean,
): string {
  if (!date) return ''

  if (isString) {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    return moment(date as string, 'YYYY-MM-DD').format('DD/MM/YYYY')
  }
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  return moment.unix(date as number).format('DD/MM/YYYY')
}

export function getVoucherValidity(
  promo: Promotion | IPromotionResponse,
): ReactElement | string {
  if (!promo.validityStart && !promo.validityEnd) return '-'

  const word = <span style={{ color: colors.darkerGray }}> to </span>
  const isPromotion = typeof promo.validityStart === 'string'

  const startDate = formatValidityDate(promo.validityStart, isPromotion)
  const endDate = formatValidityDate(promo.validityEnd, isPromotion)

  return (
    <span>
      {promo.validityStart && startDate}
      {promo.validityStart && promo.validityEnd && word}
      {promo.validityEnd && endDate}
    </span>
  )
}

export enum PromoState {
  Active = 'active',
  Inactive = 'inactive',
  Archived = 'archived',
  Scheduled = 'scheduled',
  WithinSchedule = 'active schedule',
  PastSchedule = 'past schedule',
  Draft = 'draft',
}

export const getPromoState = (promo?: UpdatePromotion): string => {
  if (!promo) return PromoState.Archived
  const missingRequired = !promo?.name || !promo?.type || !promo?.amount
  if (missingRequired) return PromoState.Draft

  const now = moment()
  const hasSchedule = promo.activeFrom
  const afterStart = promo.activeFrom
    ? now.isSameOrAfter(moment(promo.activeFrom, 'YYYY-MM-DD HH:mm:ss'))
    : false
  const beforeEnd = promo.deactivateOn
    ? now.isSameOrBefore(moment(promo.deactivateOn, 'YYYY-MM-DD HH:mm:ss'))
    : true

  const pastEnd = promo.deactivateOn
    ? now.isAfter(moment(promo.deactivateOn, 'YYYY-MM-DD HH:mm:ss'))
    : false

  if (afterStart && beforeEnd) return PromoState.WithinSchedule
  if (pastEnd) return PromoState.PastSchedule
  if (hasSchedule) return PromoState.Scheduled
  if (promo.status === PromotionStatus.Active) return PromoState.Active
  return PromoState.Inactive
}

export const isPromoActive = (promo?: UpdatePromotion): boolean => {
  const status = getPromoState(promo)
  if (status === PromoState.Active || status === PromoState.WithinSchedule) return true
  return false
}

const StyledStatusText = styled.span<{ color: string; stale?: boolean }>`
  font-weight: bold;
  color: ${({ color }): string => color};
  opacity: ${({ stale }): number => (stale ? 0.7 : 1)};
  text-wrap: nowrap;
`
export function StatusText({
  promo,
  appliedId,
  error,
  checked,
  stale,
}: {
  promo: Promotion
  appliedId?: number
  error?: string
  checked?: boolean
  stale?: boolean
}): ReactElement {
  const state = getPromoState(promo)
  const isActive = isPromoActive(promo)
  const isApplied = appliedId === promo.id

  let color = isActive ? colors.green : colors.darkerGray
  if (checked && error) color = colors.red
  if (isApplied && !error && !stale) color = colors.blue

  const label = (): string => {
    if (checked && error) return 'Error'
    if (isApplied && !stale) return 'Applied'
    return capitalize(state)
  }

  return (
    <StyledStatusText color={color} stale={stale}>
      {'\u25CF '}
      {label()}
    </StyledStatusText>
  )
}

export const getPromoDiscount = (
  promo: UpdatePromotion,
  currencyCode?: CurrencyCode,
): string => {
  if (!promo.amount) return '-'
  if (promo.type === PromotionType.Fixed) {
    return `${getCurrencySymbol(currencyCode || CurrencyCode.GBP)}${promo.amount}`
  }
  return `${promo.amount}%`
}

export function nullify(object: object): object {
  return Object.fromEntries(
    Object.entries(object).map(([k, v]) => {
      if (k === 'code' && v === '') return [k, null]
      return [k, v ?? null]
    }),
  )
}
